<template>
  <div class="details-ae">
    <header>
      <div class="hoverable-discrete-link mr-3" @click="$emit('close')">
        <b-icon size="is-normal" icon="angle-double-right"></b-icon>
      </div>
    </header>
    <div class="projet_detail">
      <div class="header-grid">
        <div>
          <h2>{{ project.titre }}</h2>
          <p>{{ project.ville }}</p>
          <p
            v-if="
              role === 'SUPER_ADMINISTRATEUR' ||
              role === 'OBSERVATEUR_NATIONAL' ||
              role === 'ACCOMPAGNATEUR_NATIONAL'
            "
          >
            <b-button type="is-primary" @click="viewProject">
              {{ $t("mapDraw.legends.project.viewProject") }}
            </b-button>
          </p>
          <p
            v-if="
              (role === 'ACCOMPAGNATEUR_REGIONAL' || role === 'ANIMATEUR_REGIONAL') &&
              showViewProject(project.graeID)
            "
          >
            <b-button type="is-primary" @click="viewProject">
              {{ $t("mapDraw.legends.project.viewProject") }}
            </b-button>
          </p>
        </div>
        <div>
          <img
            :src="icon"
            :alt="project.titre"
            width="120px"
            class="d-flex"
            style="margin-left: 30%"
          />
        </div>
      </div>
      <div class="pitch-container">
        <div>
          <strong>{{ $t("mapDraw.legends.project.pitch") }} :</strong><br />
          <div :class="{ 'line-clamp': !showFullPitch, 'scrollable-pitch': showFullPitch }">
            <span>
              {{ displayedPitch || $t("mapDraw.legends.project.not-available") }}
            </span>
          </div>
          <a v-if="project.pitch && project.pitch.length > 200" @click="togglePitch">
            {{
              showFullPitch
                ? $t("mapDraw.legends.project.showLess")
                : $t("mapDraw.legends.project.showMore")
            }}
          </a>
        </div>
      </div>
      <div>
        <strong>{{ $t("mapDraw.legends.project.createdIn") }}:</strong>
        {{ project.anneeCreation || $t("mapDraw.legends.project.not-available") }}
      </div>
      <div>
        <strong>{{ $t("mapDraw.legends.project.classes") }}:</strong>
        <ul>
          <li v-for="(classItem, index) in displayedClasses" :key="index">{{ classItem }}</li>
        </ul>
        <a v-if="project.classes && project.classes.length > 2" @click="toggleClasses">
          {{
            showFullClasses
              ? $t("mapDraw.legends.project.showLess")
              : $t("mapDraw.legends.project.showMore")
          }}
        </a>
        <span v-if="!project.classes || project.classes.length === 0">
          {{ $t("mapDraw.legends.project.not-available") }}
        </span>
      </div>
      <div>
        <strong>{{ $t("mapDraw.legends.project.establishment") }}:</strong><br />
        {{ getFirstOrUnavailable(project.nomEtablissement) }}
      </div>
      <div v-if="role === 'ENSEIGNANT' || role === 'REFERENT'">
        <strong>{{ $t("mapDraw.legends.project.teachers") }}:</strong>
        <ul>
          <li v-for="(enseignant, index) in displayedEnseignants" :key="index">
            {{ enseignant.nom }} {{ enseignant.prenom }} ({{ enseignant.courriel }})
          </li>
        </ul>
        <a v-if="visibleEnseignants.length > 2" @click="toggleEnseignants">
          {{
            showFullEnseignants
              ? $t("mapDraw.legends.project.showLess")
              : $t("mapDraw.legends.project.showMore")
          }}
        </a>
        <span v-if="visibleEnseignants.length === 0">
          {{ $t("mapDraw.legends.project.not-available") }}
        </span>
      </div>
      <div>
        <strong>{{ $t("mapDraw.legends.project.structure") }} :</strong><br />
        {{ getFirstOrUnavailable(project.nomStructure) }}
      </div>
      <div v-if="role === 'ENSEIGNANT' || role === 'REFERENT'">
        <strong>{{ $t("mapDraw.legends.project.referents") }}:</strong>
        <ul>
          <li v-for="(referent, index) in displayedReferents" :key="index">
            {{ referent.nom }} {{ referent.prenom }} ({{ referent.courriel }})
          </li>
        </ul>
        <a v-if="visibleReferents.length > 2" @click="toggleReferents">
          {{
            showFullReferents
              ? $t("mapDraw.legends.project.showLess")
              : $t("mapDraw.legends.project.showMore")
          }}
        </a>
        <span v-if="visibleReferents.length === 0">
          {{ $t("mapDraw.legends.project.not-available") }}
        </span>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType } from "vue"
import { AireEducativeCartoDetailsDTO } from "@/model/bean/GeneratedDTOs"
import { BButton, BIcon } from "buefy"

export default defineComponent({
  name: "ProjectDetails",
  components: {
    BIcon,
    BButton,
  },
  props: {
    project: {
      type: Object as PropType<AireEducativeCartoDetailsDTO>,
      required: true,
    },
    role: {
      type: String,
      required: true,
    },
    icon: {
      type: String,
      required: true,
    },
    userRegionId: {
      type: String,
      required: true,
    },
    instructedGraes: {
      type: Array as PropType<string[]>,
      required: true,
    },
  },

  emits: ["close"],
  data() {
    return {
      showFullPitch: false,
      showFullClasses: false,
      showFullEnseignants: false,
      showFullReferents: false,
      trimmedPitch: "",
      trimmedClasses: [] as string[],
      trimmedEnseignants: [] as any[],
      trimmedReferents: [] as any[],
    }
  },
  computed: {
    visibleEnseignants() {
      return this.project.enseignants?.filter(
        (enseignant) =>
          // @ts-ignore
          this.project.consentementValide.includes(enseignant.id) &&
          enseignant.acceptNameVisibilityOnProtectedListing,
      )
    },

    visibleReferents() {
      return this.project.referents?.filter(
        (referent) =>
          // @ts-ignore
          this.project.consentementValide.includes(referent.id) &&
          referent.acceptNameVisibilityOnProtectedListing,
      )
    },

    displayedClasses() {
      return this.$data.showFullClasses ? this.project.classes : this.$data.trimmedClasses
    },

    displayedPitch() {
      return this.$data.showFullPitch ? this.project.pitch : this.$data.trimmedPitch
    },

    displayedEnseignants() {
      return this.$data.showFullEnseignants
        ? (this as any).visibleEnseignants
        : this.$data.trimmedEnseignants
    },

    displayedReferents() {
      return this.$data.showFullReferents
        ? (this as any).visibleReferents
        : this.$data.trimmedReferents
    },
  },
  watch: {
    project() {
      this.setTrimmedPitch()
      this.setTrimmedClasses()
      this.setTrimmedEnseignants()
      this.setTrimmedReferents()
    },

    showFullPitch() {
      this.setTrimmedPitch()
    },

    showFullClasses() {
      this.setTrimmedClasses()
    },

    showFullEnseignants() {
      this.setTrimmedEnseignants()
    },

    showFullReferents() {
      this.setTrimmedReferents()
    },
  },
  mounted() {
    this.setTrimmedPitch()
    this.setTrimmedClasses()
    this.setTrimmedEnseignants()
    this.setTrimmedReferents()
  },
  methods: {
    getFirstOrUnavailable(arr: any[]): string {
      return Array.isArray(arr) && arr.length > 0
        ? arr[0]
        : this.$t("mapDraw.legends.project.not-available")
    },
    togglePitch() {
      this.showFullPitch = !this.showFullPitch
    },

    toggleClasses() {
      this.showFullClasses = !this.showFullClasses
    },

    toggleEnseignants() {
      this.showFullEnseignants = !this.showFullEnseignants
    },

    toggleReferents() {
      this.showFullReferents = !this.showFullReferents
    },

    showViewProject(id: string): boolean {
      return this.userRegionId === id || this.instructedGraes.includes(id)
    },

    setTrimmedPitch() {
      if (this.project.pitch && this.project.pitch.length > 300 && !this.showFullPitch) {
        this.trimmedPitch = this.project.pitch.slice(0, 300) + "..."
      } else {
        this.trimmedPitch = this.project.pitch
      }
    },

    setTrimmedClasses() {
      if (this.project.classes && this.project.classes.length > 2 && !this.showFullClasses) {
        this.trimmedClasses = this.project.classes.slice(0, 2)
      } else {
        this.trimmedClasses = this.project.classes
      }
    },

    setTrimmedEnseignants() {
      if (this.visibleEnseignants?.length > 2 && !this.showFullEnseignants) {
        this.trimmedEnseignants = this.visibleEnseignants.slice(0, 2)
      } else {
        this.trimmedEnseignants = this.visibleEnseignants
      }
    },

    setTrimmedReferents() {
      if (this.visibleReferents?.length > 2 && !this.showFullReferents) {
        this.trimmedReferents = this.visibleReferents.slice(0, 2)
      } else {
        this.trimmedReferents = this.visibleReferents
      }
    },

    viewProject() {
      this.$router.push(
        "/project/" + this.project?.idProjet + "/" + this.project?.projetAnnuelId + "/historique/0",
      )
    },
  },
})
</script>

<style lang="scss" scoped>
$primary-color: #007bff;
$background-color: #fff;
$border-radius: 10px;
$box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
$font-family: Arial, sans-serif;
$grid-gap: 10px;
$margin-small: 5px;
$margin-medium: 10px;
$padding: 20px;

.details-ae {
  background-color: $background-color;
  padding: $padding;
  border-radius: $border-radius;
  box-shadow: $box-shadow;
  text-align: left;
  width: 80%;
}

.projet_detail {
  font-family: $font-family;

  h2 {
    font-size: 1.5em;
    margin-bottom: $margin-medium;
  }

  p {
    margin: $margin-small 0;
  }

  strong {
    font-weight: bold;
  }
}

.buttons {
  .hoverable-discrete-link {
    cursor: pointer;
    color: $primary-color;
    text-decoration: underline;
  }
}

.header-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: $grid-gap;
}

.scrollable-pitch {
  max-height: 100px;
  overflow-y: auto;
}

.line-clamp {
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

ul {
  list-style-type: none;
  padding-left: 0;

  li {
    margin: $margin-small 0;
  }
}

a {
  color: $primary-color;
  cursor: pointer;
  text-decoration: underline;
}
</style>
